import React, { useMemo } from 'react';
import { Box, Paper, Typography, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close, Search } from '@mui/icons-material';
import { MatchMessageList } from './MatchMessageList';
import { MatchPanelInput } from './MatchPanelInput';
import { MatchInteractionPopup } from './MatchInteractionPopup';
import { useMatchChat } from './useMatchChat';

interface MatchPanelContentProps {
  onClose: () => void;
}

export const MatchPanelContent: React.FC<MatchPanelContentProps> = ({ onClose }) => {
  const theme = useTheme();
  const { messages, isTyping, sendMessage, selectOption, selectMultipleOptions, submitText, submitInfoGather, dismissInteraction } = useMatchChat();

  // Find the current interactive message (last assistant message awaiting response)
  const interactiveMessage = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role !== 'assistant') continue;

      if (msg.options && msg.options.length > 0 && !msg.selectedOptionId && !msg.selectedOptionIds) return msg;
      if (msg.textPrompt && !msg.textResponse) return msg;
      if (msg.infoFields && msg.infoFields.length > 0 && !msg.infoResponses) return msg;
    }
    return null;
  }, [messages]);

  return (
    <Paper
      elevation={0}
      square
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Search color="primary" fontSize="small" />
          Report Assistant
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {/* Message List */}
      <Box sx={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
        <MatchMessageList
          messages={messages}
          isTyping={isTyping}
        />
      </Box>

      {/* Interaction Popup — sits directly above the input box */}
      <MatchInteractionPopup
        message={interactiveMessage}
        onSelectOption={selectOption}
        onSelectMultipleOptions={selectMultipleOptions}
        onSubmitText={submitText}
        onSubmitInfoGather={submitInfoGather}
        onDismiss={interactiveMessage ? () => dismissInteraction(interactiveMessage.id) : undefined}
      />

      {/* Input — hidden when an interaction popup is active */}
      {!interactiveMessage && (
        <Box sx={{ zIndex: 10 }}>
          <MatchPanelInput onSend={sendMessage} disabled={isTyping} />
        </Box>
      )}
    </Paper>
  );
};
